// @ts-check
// ── imports explicites (maintenus à la main, comme les autres hooks) ──
import { useEffect, useRef, useState } from "../00-boot.jsx";
import { listingMediaRefusalText } from "../01-taxonomy-cities.jsx";
import { tx } from "../04-i18n-dict.jsx";
import { ouvrirEnvoi, rememberUploadVerdict } from "../06-images-upload.jsx";
import { etatDeNaissance } from "../07-trust-moderation.jsx";
// ═══ usePublishOutbox — LA FILE D'ENVOI DES PUBLICATIONS (F1 étape 2, 29/09/2026) ═══
//
//  CE QU'ELLE FAIT. Une publication qui n'a pas pu partir pour une raison PASSAGÈRE (panne d'envoi, plafond de photos du
//  jour, cadence, base qui tousse) n'est plus un échec : elle entre dans une file PERSISTANTE de l'appareil (IndexedDB), et
//  repart SEULE : au retour du réseau, à la réouverture de l'application, à l'heure que le serveur a donnée. Un refus
//  DÉFINITIF (taille, format, contenu, règle du serveur) n'y entre pas : le formulaire le dit et garde tout, comme avant.
//
//  ⭐ LES GARANTIES (la doctrine de l'outbox des messages, M2) :
//   · IDEMPOTENTE PAR CONSTRUCTION — une CLÉ tirée une fois (`cle` = publish_key) : un rejeu relit d'abord si l'annonce est
//     née (réponse perdue) et, si la base ne répond pas, n'envoie RIEN à l'aveugle ; la base refuse une seconde naissance
//     (index unique auteur × clé) ; ce qui est DÉJÀ PARTI (photos, verdicts, vidéo) est retenu dans `faits` ;
//   · UN À LA FOIS, RIEN DE PERDU — dans l'onglet, passes et gestes passent par UNE chaîne (un à la fois, dans l'ordre) ;
//     entre onglets, par le verrou Web Locks du compte (ATTENDU, jamais esquivé : un déclencheur ne se perd plus) ; une
//     passe demandée pendant qu'une autre attend s'y FOND (un geste passe devant un déclencheur) ;
//   · BORNÉE — au plus FILE_MAX envois par compte ; SEULE, la file ne rejoue ni au-delà de 24 h, ni après ESSAIS_SEULS_MAX
//     échecs (une vidéo de 50 Mo ne repart pas cent fois sur un réseau qui hoquette) : elle attend un geste ; une limite de
//     COMPTE (plafond, cadence) arrête la passe ; hors ligne, aucune minuterie ne tourne (le retour du réseau suffit).
//     Interrupteur : localStorage `lanac:file-annonces` = "0" — rien n'entre plus dans la file (le formulaire se comporte
//     comme avant l'étape 2) et rien ne repart seul ; un geste reste possible sur ce qui y est.
//   · ÉCONOME — deux magasins : `envois` (l'état, réécrit à chaque essai, quelques Ko) et `fichiers` (photos, vidéo,
//     brouillon : écrits UNE fois, jamais réécrits par un essai).
//
//  ⭐ F1 ÉTAPE 3 (29/09/2026) — LES CINQ RESTES :
//   · UNE ANNONCE SUPPRIMÉE NE RENAÎT PAS — la base tient le REGISTRE des clés (migration 20260929194912) : une réponse
//     perdue, puis l'annonce retirée depuis un autre appareil, puis le rejeu — hier elle renaissait ; la base la refuse
//     (`publication_supprimee`), la file la retire et le DIT ;
//   · UN ENVOI QUI SE TAIT EST MORT — chien de garde sur la progression (06), écritures bornées (07) : une connexion qui
//     cale ne tient plus la file ; la progression de l'envoi se voit (`progres`) ;
//   · L'APPAREIL GARDE — le stockage est demandé PERSISTANT (une fois par appareil) ; et la file s'EFFACE d'elle-même : un
//     envoi refusé 7 jours après son refus, tout envoi 30 jours après sa création — la date est dite ;
//   · LA DÉCONNEXION ABANDONNE l'essai en vol (rien de plus n'est envoyé ni écrit) ; il repartira sous son compte ;
//   · LE BOOST SE REVALIDE AU REJEU — « aujourd'hui » ne vaut que le jour même, une date choisie qu'à plus d'une heure de sa
//     fin ; une durée (24 h, 3 j, 7 j) court depuis l'octroi. Sinon : publiée sans Boost, aucun crédit consommé, et DIT.
//
//  Per-user-state-guard : AUCUN état de module mutable — tout vit dans le hook ou dans IndexedDB, clé = la clé de
//  publication, chaque envoi porte son `uid` (un autre compte sur le même appareil ne le voit ni ne le rejoue).

const FILE_IDB_DB="lanac-file-publications", FILE_ENVOIS="envois", FILE_FICHIERS="fichiers", FILE_IDB_V=2;
const FILE_MAX=10;
const REJEU_SEUL_MS=24*3600*1000;
const ESSAIS_SEULS_MAX=8;
const RECORD_V=2;
const EFFACEE_REFUSEE_MS=7*86400000, EFFACEE_MS=30*86400000;
const BOOST_MARGE_MS=3600000;
const fileIdbOpen=()=>new Promise((res,rej)=>{
  try{
    if(typeof indexedDB==="undefined"||!indexedDB) return rej(new Error("no idb"));
    const rq=indexedDB.open(FILE_IDB_DB,FILE_IDB_V);
    rq.onupgradeneeded=()=>{
      const db=rq.result;
      for(const s of [FILE_ENVOIS,FILE_FICHIERS]) if(!db.objectStoreNames.contains(s)){try{db.createObjectStore(s);}catch(e){}}
    };
    rq.onsuccess=()=>res(rq.result);
    rq.onerror=()=>rej(rq.error||new Error("idb open"));
    rq.onblocked=()=>rej(new Error("idb blocked"));
  }catch(e){rej(e);}
});
//  Une transaction sur un ou deux magasins : `fn` reçoit les magasins par nom et rend la requête dont le résultat compte.
const fileIdbCall=async(magasins,mode,fn)=>{
  const db=await fileIdbOpen();
  try{
    return await new Promise((res,rej)=>{
      const t=db.transaction(magasins,mode);
      const st=Object.fromEntries(magasins.map(m=>[m,t.objectStore(m)]));
      const rq=fn(st);
      t.oncomplete=()=>res(rq&&("result" in rq)?rq.result:undefined);
      t.onerror=()=>rej(t.error||new Error("idb tx"));
      t.onabort=()=>rej(t.error||new Error("idb abort"));
    });
  }finally{try{db.close();}catch(e){}}
};
const fileLireTout=async uid=>((await fileIdbCall([FILE_ENVOIS],"readonly",st=>st[FILE_ENVOIS].getAll()))||[]).filter(r=>r&&r.v===RECORD_V&&r.uid===String(uid));
const fileLireFichiers=cle=>fileIdbCall([FILE_FICHIERS],"readonly",st=>st[FILE_FICHIERS].get(cle));
const fileEcrireEtat=rec=>fileIdbCall([FILE_ENVOIS],"readwrite",st=>st[FILE_ENVOIS].put(rec,rec.cle));
const fileEcrireTout=(rec,fichiers)=>fileIdbCall([FILE_ENVOIS,FILE_FICHIERS],"readwrite",st=>{st[FILE_FICHIERS].put(fichiers,rec.cle);return st[FILE_ENVOIS].put(rec,rec.cle);});
const fileSupprimer=cle=>fileIdbCall([FILE_ENVOIS,FILE_FICHIERS],"readwrite",st=>{st[FILE_FICHIERS].delete(cle);return st[FILE_ENVOIS].delete(cle);});

//  ── PARTIE PURE (bancable) ──────────────────────────────────────────────────────────────────────────────────────────
//  Ce qui est PASSAGER, et quand réessayer. Le serveur dit le délai d'un plafond (secondes) ; une cadence dure une minute ;
//  une panne se réessaie au retour du réseau, et entre-temps de plus en plus loin (15 s → 15 min). Tout le reste est un
//  REFUS : il se dit, il ne se rejoue pas.
//  ⚠️ Une erreur de la base n'a pas toujours de code : une page HTML de passerelle (502, 503, 520…) arrive en `{message}`
//   seul (postgrest-js 2.110, lu dans le vendor) — c'est le STATUT HTTP, porté à côté, qui dit la panne.
//  `deja_nee_illisible` : l'index a dit l'annonce NÉE, la relecture n'a pas pu la rendre — le prochain essai relira.
//  `abandon` (la déconnexion a coupé l'essai) et `hors_delai` (une écriture bornée n'a pas répondu) : des pannes (F1 étape 3).
const TRANSITOIRES={upload_failed:"panne",offline:"panne",deja_nee_illisible:"panne",abandon:"panne",hors_delai:"panne",rate_limited:"cadence",media_quota:"plafond"};
//  La base ou son chemin qui toussent : pool de connexions (PGRST000–003), jeton à rafraîchir (PGRST301/303), temps
//  dépassé (57014), arrêt/redémarrage (57P01–57P03), sérialisation et verrous (40001, 40P01, 55P03) ; classes 08
//  (connexion) et 53 (ressources) entières.
const CODES_PANNE=new Set(["PGRST000","PGRST001","PGRST002","PGRST003","PGRST301","PGRST303","57014","57P01","57P02","57P03","40001","40P01","55P03"]);
const classerEchecPublication=err=>{
  const code=String(err?.code??"");
  if(Object.prototype.hasOwnProperty.call(TRANSITOIRES,code)){
    const motif=TRANSITOIRES[code];
    return {transitoire:true,motif,apresMs:motif==="plafond"?Math.max(60,Number(err?.secondes)||0)*1000:motif==="cadence"?60000:0};
  }
  if(CODES_PANNE.has(code)||/^(08|53)[0-9A-Z]{3}$/.test(code)) return {transitoire:true,motif:"panne",apresMs:0};
  const statut=Number(err?.status)||0, msg=String(err?.message??err??"");
  //  Un statut de panne SANS code de règle (une contrainte, un droit, une donnée refusée gardent leur verdict).
  if(!/^(2[2-3]|42|P0)/.test(code)&&(statut===408||statut>=500)) return {transitoire:true,motif:"panne",apresMs:0};
  if(!code&&statut===429) return {transitoire:true,motif:"cadence",apresMs:60000};
  if(!code&&(/failed to fetch|networkerror|load failed|network request failed|fetch failed|aborted|timeout|timed out|client unavailable/i.test(msg)||/^\s*<(!doctype|html)/i.test(msg)))
    return {transitoire:true,motif:"panne",apresMs:0};
  return {transitoire:false,motif:"refus",apresMs:0};
};
const PALIERS_PANNE_MS=[15000,30000,60000,120000,300000,600000,900000];
const prochainEssaiDans=(essais,cls)=>cls.motif==="panne"?PALIERS_PANNE_MS[Math.min(Math.max(0,essais-1),PALIERS_PANNE_MS.length-1)]:Math.max(cls.apresMs,5000);
const estUnBlob=b=>!!b&&typeof b.arrayBuffer==="function";
//  Un envoi en file : son ÉTAT (la publication sans ses Blobs, ce qui est parti, l'échéance) …
const recordDePublication=(item,{cle,uid,brouillon=null,faits=null,erreur=null,maintenant=Date.now()})=>{
  const {photoFiles,videoFile,...reste}=item||{};
  const cls=classerEchecPublication(erreur);
  return {v:RECORD_V,cle,uid:String(uid),creeLe:maintenant,titre:String(reste.title||""),
    item:{...reste,id:cle},
    faits:{photos:{...(faits?.photos||{})},video:faits?.video||null,insertTente:!!faits?.insertTente},
    aBrouillon:!!brouillon,aVignette:Array.isArray(photoFiles)&&photoFiles.some(estUnBlob),
    essais:1,prochainEssai:maintenant+prochainEssaiDans(1,cls),etat:"attente",
    dernier:erreur?{code:String(erreur.code??""),motif:cls.motif,secondes:Number(erreur.secondes)||0,message:String(erreur.message??"").slice(0,200)}:null};
};
//  … et ses FICHIERS (photos, vidéo, brouillon), écrits une fois à part.
const fichiersDePublication=(item,brouillon=null)=>({
  photos:Array.isArray(item?.photoFiles)?item.photoFiles.map(b=>estUnBlob(b)?b:null):[],
  video:estUnBlob(item?.videoFile)?item.videoFile:null,
  brouillon:brouillon||null});
//  La publication à rejouer : ce qui est parti devient l'adresse (et le Blob n'est plus envoyé), le reste repart.
const itemDepuisRecord=(r,fichiers=null)=>{
  const faits=r.faits||{photos:{},video:null};
  const photos=(r.item.photos||[]).map((p,i)=>faits.photos?.[i]?.url||p);
  const photoFiles=(fichiers?.photos||[]).map((b,i)=>faits.photos?.[i]?.url?null:b);
  let item={...r.item,id:r.cle,photos,photoFiles,img:photos[0]||r.item.img};
  if(faits.video) item={...item,...faits.video,videoFile:null};
  else if(fichiers?.video) item={...item,videoFile:fichiers.video};
  return item;
};
//  La clé d'une publication : l'uuid que le formulaire a tiré (item.id), sinon un neuf ; null si l'appareil n'en sait pas
//   tirer (alors ni clé, ni file : le formulaire dit l'échec, comme avant).
const cleDePublication=item=>{
  const id=String(item?.id??"");
  if(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) return id.toLowerCase();
  try{return typeof crypto!=="undefined"&&crypto.randomUUID?crypto.randomUUID():null;}catch(e){return null;}
};
//  Ce que l'écran dit quand une publication entre dans la file : pourquoi, et quand elle partira. Une PANNE ne se dit
//   « pas de connexion » que si l'appareil est hors ligne — en ligne, c'est l'envoi qui n'a pas abouti.
const cleTexteMiseEnFile=(cls,enLigne=true)=>cls.motif==="plafond"?"tax.plafond-de-photos-du-jou_ca27f2":cls.motif==="cadence"?"tax.trop-d-envois-en-peu-de_698efe"
  :enLigne?"tax.l-envoi-n-a-pas-abouti-v_dc32f1":"tax.pas-de-connexion-votre-a_b6867a";
//  SEULE, la file ne rejoue que ce qui attend, depuis moins de 24 h, et moins de ESSAIS_SEULS_MAX fois.
const peutRejouerSeul=(r,maintenant=Date.now())=>r.etat==="attente"&&maintenant-r.creeLe<=REJEU_SEUL_MS&&(Number(r.essais)||0)<ESSAIS_SEULS_MAX;
//  Qui part dans une passe. Un GESTE (« manuel ») rejoue tout ce qui n'est pas refusé. Seule, la file ne rejoue qu'à l'heure
//   dite — sauf une PANNE au retour du réseau : son délai n'attendait que lui (celui d'un plafond ou d'une cadence est celui
//   du SERVEUR, il ne se raccourcit pas).
const doitPartir=(r,motif,maintenant=Date.now())=>{
  if(r.etat==="refusee") return false;
  if(motif==="manuel") return true;
  if(!peutRejouerSeul(r,maintenant)) return false;
  if(motif==="reseau"&&(r.dernier?.motif||"panne")==="panne") return true;
  return r.prochainEssai<=maintenant;
};
//  L'état DIT, calculé à l'instant où on le lit (l'écran le recalcule en vivant) — et POURQUOI il attend un geste.
const etatAffiche=(p,maintenant=Date.now())=>p.etat==="attente"&&!peutRejouerSeul(p,maintenant)?"a_confirmer":p.etat;
const pourquoiAConfirmer=(p,maintenant=Date.now())=>maintenant-p.creeLe>REJEU_SEUL_MS?"anciennete":"essais";
const resumeDeRecord=(r,maintenant=Date.now())=>({cle:r.cle,titre:r.titre,creeLe:r.creeLe,prochainEssai:r.prochainEssai,
  essais:Number(r.essais)||0,etat:etatAffiche(r,maintenant),motif:r.dernier?.motif||"panne",
  secondes:r.dernier?.secondes||0,dernier:r.dernier||null,aVignette:!!r.aVignette,
  photoUrl:Object.values(r.faits?.photos||{})[0]?.url||null,peutReprendre:!!r.aBrouillon,effaceeLe:effaceeLe(r)});
//  Le jour où l'appareil l'EFFACE : 7 jours après son refus, 30 jours après sa création (le premier des deux).
const effaceeLe=r=>{
  const cree=Number(r.creeLe)||0;
  return Math.min(cree+EFFACEE_MS,r.etat==="refusee"?(Number(r.refuseeLe)||cree)+EFFACEE_REFUSEE_MS:Infinity);
};
//  Le Boost voulu, revalidé à l'heure du REJEU : « aujourd'hui » (sa fin = la fin du jour choisi) ne vaut que ce jour-là ;
//   une date choisie, qu'à plus d'une heure de sa fin ; une durée court depuis l'octroi (le serveur la calcule). Sinon, la
//   publication part SANS Boost — comme le formulaire l'écrit quand il n'y en a pas (`{active:false}`) — et le rejeu le dira.
const boostAuRejeu=(item,maintenant=Date.now())=>{
  const b=item?.boostSale;
  if(!b||b.active!==true) return {item,retire:false};
  const fin=Number(b.endsAt)||0;
  const vaut=b.duration==="today"?fin>maintenant:b.duration==="custom"?fin>maintenant+BOOST_MARGE_MS:true;
  return vaut?{item,retire:false}:{item:{...item,boostSale:{active:false}},retire:true};
};
//  La progression d'un essai : les octets À ENVOYER (les Blobs pas encore partis), et ce qui en est parti — un envoi fini
//   s'ajoute, l'envoi en vol compte pour ce qu'il a chargé. Un pourcentage ENTIER, jamais 100 avant la naissance.
const octetsAEnvoyer=(r,fichiers)=>{
  const faits=r.faits||{photos:{},video:null};
  let n=0;
  (fichiers?.photos||[]).forEach((b,i)=>{if(estUnBlob(b)&&!faits.photos?.[i]?.url)n+=Number(b.size)||0;});
  if(!faits.video&&estUnBlob(fichiers?.video))n+=Number(fichiers.video.size)||0;
  return n;
};
const suiviDeProgres=(total,surPourcent)=>{
  let partis=0,dernier=-1;
  return (charge,taille,fin)=>{
    const enVol=fin?0:Math.max(0,Number(charge)||0);
    if(fin) partis+=Math.max(0,Number(taille)||0);
    const pct=total>0?Math.min(99,Math.floor(100*(partis+enVol)/total)):0;
    if(pct!==dernier){dernier=pct;surPourcent(pct);}
  };
};
//  L'empreinte de ce que l'écran montre : la file ne re-rend App que si elle a CHANGÉ (une passe relit deux fois).
const signatureDeFile=resumes=>resumes.map(p=>[p.cle,p.etat,p.essais,p.prochainEssai,p.motif,p.dernier?.code||""].join("|")).join("\n");

//  ── LE HOOK ─────────────────────────────────────────────────────────────────────────────────────────────────────────
/**
 * @param {{uid:any, publierRef:{current:Function|null}, notify:Function, lang:string}} cfg
 */
function usePublishOutbox({uid,publierRef,notify,lang}){
  const [enAttente,setEnAttente]=useState([]);
  const [enCours,setEnCours]=useState(null);
  const [progres,setProgres]=useState(null);   // {cle, pourcent} de l'essai en vol
  const abandonRef=useRef(null);               // l'AbortController de l'essai en vol (la déconnexion l'abandonne)
  const chaineRef=useRef(Promise.resolve());   // l'onglet : passes et gestes, un à la fois, dans l'ordre
  const demandeRef=useRef(null);               // la passe demandée et pas encore commencée : les demandes s'y fondent
  const signatureRef=useRef(null);
  const minuterieRef=useRef(null);
  const uidRef=useRef(uid);uidRef.current=uid;
  const langRef=useRef(lang);langRef.current=lang;
  const notifyRef=useRef(notify);notifyRef.current=notify;
  const enLigne=()=>typeof navigator==="undefined"||navigator.onLine!==false;

  const relire=async()=>{
    const u=uidRef.current;
    let tous=[];
    if(u){try{tous=await fileLireTout(u);}catch(e){tous=[];}}
    tous.sort((a,b)=>a.creeLe-b.creeLe);
    const resumes=tous.map(r=>resumeDeRecord(r)),sig=signatureDeFile(resumes);
    if(sig!==signatureRef.current){signatureRef.current=sig;setEnAttente(resumes);}
    return tous;
  };
  const armerMinuterie=tous=>{
    clearTimeout(minuterieRef.current);
    if(!enLigne()) return;                     // hors ligne : c'est le retour du réseau qui relancera, pas une horloge
    const proches=tous.filter(r=>peutRejouerSeul(r)).map(r=>r.prochainEssai);
    if(!proches.length) return;
    const dans=Math.min(Math.max(Math.min(...proches)-Date.now(),5000),900000);
    minuterieRef.current=setTimeout(()=>{passe("minuterie");},dans);
  };
  const interrupteurCoupe=()=>{try{return localStorage.getItem("lanac:file-annonces")==="0";}catch(e){return false;}};
  //  Le verrou du COMPTE entre onglets (Web Locks, ATTENDU) ; sans Web Locks, la chaîne de l'onglet et l'idempotence.
  const sousVerrou=(u,fn)=>typeof navigator!=="undefined"&&navigator.locks&&navigator.locks.request
    ?navigator.locks.request(`lanac-file-publications:${u}`,async()=>fn()):fn();
  const exclusif=(u,fn)=>{
    const p=chaineRef.current.then(()=>sousVerrou(u,fn));
    chaineRef.current=p.catch(()=>{});
    return p;
  };

  //  Un essai : l'ardoise porte ce qui est déjà parti ; le verdict de chaque photo partie est rendu à la mémoire de
  //   modération (sinon une photo signalée redeviendrait « propre » après un redémarrage). Rend le classement de l'échec.
  //  ⚠️ LE COMPTE PEUT CHANGER PENDANT L'ESSAI (la déconnexion garde l'application montée). La passe ne lance que les envois
  //   de SON compte (lus pour lui, compte revérifié avant chaque essai) ; l'issue n'est ÉCRITE ni DITE que si ce compte est
  //   toujours là — sinon un refus né de la déconnexion (l'écriture partie sans session) marquerait l'envoi « refusé » et
  //   dirait son titre au compte suivant. Parti, il reste tel quel : il repartira quand son compte reviendra.
  //  ⭐ F1 étape 3 — l'essai porte un SIGNAL (la déconnexion l'abandonne : l'envoi en vol, la relecture, l'écriture) et
  //   sa PROGRESSION ; le Boost est revalidé à l'heure du rejeu ; une annonce SUPPRIMÉE entre-temps sort de la file et se
  //   dit. Le compte parti, ce qui est DÉJÀ parti (photos, vidéo, écriture tentée) est gardé — rien ne repartira pour rien.
  const tenter=async r=>{
    const publier=publierRef.current;if(!publier) return null;
    setEnCours(r.cle);
    const ctrl=typeof AbortController!=="undefined"?new AbortController():null;
    abandonRef.current=ctrl;
    const envoi={...ouvrirEnvoi(),signal:ctrl?ctrl.signal:null,surProgres:/** @type {null|((charge:number,taille:number,fin:boolean)=>void)} */(null),
      faits:{photos:{...(r.faits?.photos||{})},video:r.faits?.video||null,insertTente:!!r.faits?.insertTente}};
    for(const f of Object.values(envoi.faits.photos))if(f&&f.url&&f.verdict)rememberUploadVerdict(f.url,f.verdict);
    let res=null,boostRetire=false;
    try{
      let fichiers=null;try{fichiers=await fileLireFichiers(r.cle);}catch(e){fichiers=null;}
      const total=octetsAEnvoyer(r,fichiers);
      if(total>0){setProgres({cle:r.cle,pourcent:0});envoi.surProgres=suiviDeProgres(total,pourcent=>setProgres({cle:r.cle,pourcent}));}
      const revu=boostAuRejeu(itemDepuisRecord(r,fichiers));
      boostRetire=revu.retire;
      res=await publier(revu.item,{envoi,cle:r.cle,dejaTente:true});
    }catch(e){res={ok:false,erreur:{message:String(e?.message||e)}};}
    finally{setEnCours(null);setProgres(null);if(abandonRef.current===ctrl)abandonRef.current=null;}
    const memeCompte=uidRef.current===r.uid;
    if(res&&res.ok){
      try{await fileSupprimer(r.cle);}catch(e){}
      if(boostRetire&&memeCompte) notifyRef.current?.(tx("tax.votre-annonce-titre-est_70c485",langRef.current).replace("{titre}",()=>r.titre),"var(--color-primary)");
      return null;
    }
    if(!memeCompte){try{await fileEcrireEtat({...r,faits:envoi.faits});}catch(e){}return null;}
    if(res?.erreur?.code==="publication_supprimee"){
      try{await fileSupprimer(r.cle);}catch(e){}
      notifyRef.current?.(tx("tax.votre-annonce-titre-a-et_5d0b19",langRef.current).replace("{titre}",()=>r.titre),"var(--color-primary)");
      return {transitoire:false,motif:"supprimee",apresMs:0};
    }
    const cls=classerEchecPublication(res?.erreur);
    const suivant={...r,faits:envoi.faits,essais:(r.essais||0)+1,
      dernier:{code:String(res?.erreur?.code??""),motif:cls.motif,secondes:Number(res?.erreur?.secondes)||0,message:String(res?.erreur?.message??"").slice(0,200)}};
    if(cls.transitoire) suivant.prochainEssai=Date.now()+prochainEssaiDans(suivant.essais,cls);
    else{
      suivant.etat="refusee";
      suivant.refuseeLe=Date.now();
      const raison=listingMediaRefusalText(res?.erreur,langRef.current)||tx("tax.la-publication-a-ete-ref_2d02ef",langRef.current);
      notifyRef.current?.(tx("tax.votre-annonce-titre-n-a_48d5cf",langRef.current).replace("{titre}",()=>r.titre).replace("{raison}",()=>raison),"var(--color-error)");
    }
    try{await fileEcrireEtat(suivant);}catch(e){}
    return cls;
  };
  //  Une passe : dans l'ordre d'arrivée ; ce qu'un GESTE a demandé part, et, pour un déclencheur, ce qui est dû.
  const deroulerPasse=async d=>{
    try{
      let tous=await relire();
      const perimes=tous.filter(r=>effaceeLe(r)<=Date.now());
      if(perimes.length){for(const r of perimes){try{await fileSupprimer(r.cle);}catch(e){}}tous=await relire();}
      for(const r of tous){
        if(uidRef.current!==d.u) break;                                 // le compte a changé en route
        const part=d.tousManuels||d.manuels.has(r.cle)?doitPartir(r,"manuel"):(d.auto?doitPartir(r,d.auto):false);
        if(!part) continue;
        if(!enLigne()) break;
        const cls=await tenter(r);
        if(cls&&(cls.motif==="plafond"||cls.motif==="cadence")) break;  // une limite de COMPTE : les suivants la heurteraient aussi
      }
    }finally{armerMinuterie(await relire());}
  };
  //  Demander une passe. Une passe demandée et pas encore commencée ACCUEILLE les demandes suivantes (jamais perdues, jamais
  //   dix passes pour dix évènements) ; « reseau » prime parmi les déclencheurs (il libère les pannes).
  const passe=(motif,seulement=null)=>{
    const u=uidRef.current;if(!u) return Promise.resolve();
    if(motif!=="manuel"&&interrupteurCoupe()) return relire().then(()=>{});
    let d=demandeRef.current;
    if(!d||d.u!==u){
      const neuve={u,manuels:new Set(),tousManuels:false,auto:null,promesse:null};
      neuve.promesse=exclusif(u,async()=>{if(demandeRef.current===neuve)demandeRef.current=null;await deroulerPasse(neuve);}).catch(()=>{});
      demandeRef.current=neuve;d=neuve;
    }
    if(motif==="manuel"){if(seulement)d.manuels.add(seulement);else d.tousManuels=true;}
    else if(!d.auto||motif==="reseau") d.auto=motif;
    return d.promesse;
  };

  //  Déclencheurs : l'arrivée du compte (et la réouverture), le retour du réseau, le retour à l'écran, l'échéance.
  useEffect(()=>{
    signatureRef.current=null;
    if(!uid){setEnAttente([]);return;}
    const depart=setTimeout(()=>{passe("ouverture");},3000);   // laisser l'application se poser (session, flux)
    const enLigneRetour=()=>{passe("reseau");};
    const visible=()=>{if(document.visibilityState==="visible")passe("visible");};
    window.addEventListener("online",enLigneRetour);
    document.addEventListener("visibilitychange",visible);
    return ()=>{
      clearTimeout(depart);clearTimeout(minuterieRef.current);window.removeEventListener("online",enLigneRetour);document.removeEventListener("visibilitychange",visible);
      //  Le compte change (déconnexion) ou l'application se démonte : l'essai en vol est ABANDONNÉ — rien de plus ne part.
      try{abandonRef.current?.abort();}catch(e){}
    };
  },[uid]);

  //  ⭐ L'APPAREIL GARDE : sans stockage PERSISTANT, le navigateur peut effacer IndexedDB sous pression d'espace — la file
  //   avec. Demandé UNE fois par appareil (Firefox demande à la personne : jamais deux fois), jamais attendu.
  const demanderPersistance=()=>{
    try{
      const st=typeof navigator!=="undefined"?navigator.storage:null;
      if(!st||typeof st.persist!=="function") return;
      if(localStorage.getItem("lanac:stockage-persistant")) return;
      localStorage.setItem("lanac:stockage-persistant","1");
      Promise.resolve(typeof st.persisted==="function"?st.persisted():false).then(deja=>deja||st.persist()).catch(()=>{});
    }catch(e){}
  };
  //  L'API rendue à l'application.
  //  Mettre en file : rend {ok:true} ou {ok:false, raison} — « coupee » (l'interrupteur : le formulaire se comporte comme avant
  //   l'étape 2), « pleine » (FILE_MAX envois attendent déjà), « stockage » (l'appareil n'a pas pu écrire), « sans_cle ».
  const mettreEnFile=async(item,{cle=null,envoi=null,brouillon=null,erreur=null}={})=>{
    const u=uidRef.current;if(!u||!cle) return {ok:false,raison:"sans_cle"};
    if(interrupteurCoupe()) return {ok:false,raison:"coupee"};
    try{
      const tous=await fileLireTout(u);
      if(tous.length>=FILE_MAX&&!tous.some(r=>r.cle===cle)) return {ok:false,raison:"pleine"};
      await fileEcrireTout(recordDePublication(item,{cle,uid:u,brouillon,faits:envoi?.faits||null,erreur}),fichiersDePublication(item,brouillon));
    }catch(e){return {ok:false,raison:"stockage"};}
    demanderPersistance();
    armerMinuterie(await relire());
    return {ok:true};
  };
  const relancer=cle=>passe("manuel",cle);
  //  Un GESTE sur un envoi (supprimer, reprendre), dans la chaîne ET sous le verrou du compte : aucune passe ne l'envoie
  //   pendant qu'on le retire. ⭐ Et d'abord : l'annonce est-elle DÉJÀ NÉE ? (une réponse perdue laisse une annonce EN LIGNE
  //   derrière un envoi « en attente » — la reprendre en ferait naître une seconde, la supprimer dirait « pas publiée » d'une
  //   annonce publiée). Née : on ACHÈVE sa publication (ses suites n'ont jamais eu lieu) et le geste rend « publiee ». La base
  //   ne répond pas et une écriture a pu partir : « verification » (le geste attend le réseau). Rendu : « retiree »,
  //   « reprise », « publiee », « verification », « absente » (déjà partie), « echec ».
  const geste=(cle,action)=>{
    const u=uidRef.current;if(!u) return Promise.resolve("absente");
    return exclusif(u,async()=>{
      const r=(await fileLireTout(u)).find(x=>x.cle===cle);
      if(!r) return "absente";
      const naissance=enLigne()?await etatDeNaissance(r.item?.ownerId,cle):"inconnue";
      if(naissance==="nee"){await tenter(r);return "publiee";}
      if(naissance==="inconnue"&&r.faits?.insertTente) return "verification";
      return action(r);
    }).catch(()=>"echec").finally(async()=>{armerMinuterie(await relire());});
  };
  const retirer=cle=>geste(cle,async()=>{await fileSupprimer(cle);return "retiree";});
  //  Reprendre : l'envoi SORT de la file et son brouillon est rendu à l'écran (`ecrireBrouillon`) ; si l'écran ne peut pas
  //   l'écrire, l'envoi RENTRE tel quel — jamais perdu, jamais envoyé pendant qu'on le reprend.
  const reprendre=(cle,ecrireBrouillon)=>geste(cle,async r=>{
    let fichiers=null;try{fichiers=await fileLireFichiers(cle);}catch(e){fichiers=null;}
    if(!fichiers?.brouillon) return "echec";
    await fileSupprimer(cle);
    let ok=false;try{ok=(await ecrireBrouillon(fichiers.brouillon))!==false;}catch(e){ok=false;}
    if(!ok){try{await fileEcrireTout(r,fichiers);}catch(e){}return "echec";}
    return "reprise";
  });
  //  La vignette d'un envoi (sa première photo), lue à la demande : la liste ne transporte aucun Blob.
  const lireVignette=async cle=>{try{return (await fileLireFichiers(cle))?.photos?.find(estUnBlob)||null;}catch(e){return null;}};
  return {enAttente,enCours,progres,mettreEnFile,relancer,retirer,reprendre,lireVignette};
}

export { usePublishOutbox, boostAuRejeu, classerEchecPublication, cleDePublication, effaceeLe, octetsAEnvoyer, suiviDeProgres, cleTexteMiseEnFile, doitPartir, etatAffiche, fichiersDePublication, pourquoiAConfirmer, prochainEssaiDans, recordDePublication, itemDepuisRecord, peutRejouerSeul, resumeDeRecord, signatureDeFile, BOOST_MARGE_MS, EFFACEE_MS, EFFACEE_REFUSEE_MS, ESSAIS_SEULS_MAX, FILE_MAX, REJEU_SEUL_MS, PALIERS_PANNE_MS };
